/**
 * Ashtakavarga (Sarvashtakavarga) house-strength confirmation layer.
 *
 * Vol_09 gives Ashtakavarga a major role: a natal house with a high combined Rekha count
 * (Sarvashtakavarga) delivers its significations in full, while a house under the threshold
 * is weak and "struggles to deliver its full results even if the house lord is strong"
 * (Vol_09 §11.1). Vol_09 §11.3 adds the synthesis rule: the Sarvashtakavarga of the *dasha
 * lord's natal sign* modifies how fully that dasha delivers the chart's promises.
 *
 * For birth-time rectification this is a confirmation layer, not a primary judge: for each
 * dated event we reward candidate birth times whose relevant event house carries the strength
 * to deliver that event. Because the house→sign mapping and the Ascendant (one of the eight
 * contributing sources) both depend on the rising sign, the per-house Sarvashtakavarga shifts
 * across the day at rising-sign resolution — so it helps separate candidate ascendants without
 * adding second-level noise. It is applied as a gentle MULTIPLIER on the event's KP score, in
 * keeping with the engine's design (strength/quality gate delivery; they do not add raw votes).
 *
 * Calibration note. The gate compares an event house's Sarvashtakavarga to the chart's OWN
 * 12-sign mean (a relative deviation), never to an absolute textbook threshold. So the well-known
 * ±1 textual variant in some planets' Bhinnashtakavarga totals (Venus is printed as 51 or 52 in
 * different editions) cannot affect the result: it shifts every sign by the same constant and the
 * mean absorbs it. The contributor tables below are the standard Parashari set; BHINNA_TOTALS
 * records their exact per-planet maxima and the test asserts the tables reproduce them.
 */

import { SIGN_RULERS } from "../astro/constants";
import type { RawChartData } from "../astro/models";
import { pymod, pythonRound } from "./pyutil";

// Contributing sources: the seven classical planets plus the Ascendant ("asc"). Rahu/Ketu do
// NOT contribute to Ashtakavarga (Vol_09 §3.2). The 7-planet Sarvashtakavarga is the sum of
// these seven Bhinnashtakavarga charts; the Ascendant participates only as a source, not as its
// own summed chart (max 337 across 12 signs).
export const BAV_PLANETS = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn"] as const;
const _SOURCES = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn", "asc"] as const;

// Standard Parashari Bhinnashtakavarga: for each planet's own chart, the benefic places
// (1..12, counted FROM each contributing source's natal sign) at which that source awards a
// Rekha. Construction (Vol_09 §3.1), exactly as bhinnashtakavarga() implements it: in planet
// P's chart, each contributing source S awards +1 to the SIGN standing at each listed position
// p counted inclusively from S's OWN natal sign — i.e. sign (S_sign + p − 1) mod 12 gains the
// bindu. The bindu lands AT the counted-to position, NOT back on the source's own sign; an
// earlier revision of this comment described that transposed convention, which would read as
// the tables being inverted and invite a catastrophic "fix". The tables below are correct for
// the counted-to convention and are pinned cell-by-cell (totals: grand 337, per-planet
// BHINNA_TOTALS) — do not transpose them.
export const BAV_TABLE: Record<string, Record<string, readonly number[]>> = {
  sun: {
    sun: [1, 2, 4, 7, 8, 9, 10, 11],
    moon: [3, 6, 10, 11],
    mars: [1, 2, 4, 7, 8, 9, 10, 11],
    mercury: [3, 5, 6, 9, 10, 11, 12],
    jupiter: [5, 6, 9, 11],
    venus: [6, 7, 12],
    saturn: [1, 2, 4, 7, 8, 9, 10, 11],
    asc: [3, 4, 6, 10, 11, 12],
  },
  moon: {
    sun: [3, 6, 7, 8, 10, 11],
    moon: [1, 3, 6, 7, 10, 11],
    mars: [2, 3, 5, 6, 9, 10, 11],
    mercury: [1, 3, 4, 5, 7, 8, 10, 11],
    // Classical row is 1,4,7,8,10,11,12 — an earlier transcription had "2" for "12"
    // (same element count, so the totals checksum couldn't catch it).
    jupiter: [1, 4, 7, 8, 10, 11, 12],
    venus: [3, 4, 5, 7, 9, 10, 11],
    saturn: [3, 5, 6, 11],
    asc: [3, 6, 10, 11],
  },
  mars: {
    sun: [3, 5, 6, 10, 11],
    moon: [3, 6, 11],
    mars: [1, 2, 4, 7, 8, 10, 11],
    mercury: [3, 5, 6, 11],
    jupiter: [6, 10, 11, 12],
    venus: [6, 8, 11, 12],
    saturn: [1, 4, 7, 8, 9, 10, 11],
    asc: [1, 3, 6, 10, 11],
  },
  mercury: {
    sun: [5, 6, 9, 11, 12],
    moon: [2, 4, 6, 8, 10, 11],
    mars: [1, 2, 4, 7, 8, 9, 10, 11],
    mercury: [1, 3, 5, 6, 9, 10, 11, 12],
    jupiter: [6, 8, 11, 12],
    venus: [1, 2, 3, 4, 5, 8, 9, 11],
    saturn: [1, 2, 4, 7, 8, 9, 10, 11],
    asc: [1, 2, 4, 6, 8, 10, 11],
  },
  jupiter: {
    sun: [1, 2, 3, 4, 7, 8, 9, 10, 11],
    moon: [2, 5, 7, 9, 11],
    mars: [1, 2, 4, 7, 8, 10, 11],
    mercury: [1, 2, 4, 5, 6, 9, 10, 11],
    jupiter: [1, 2, 3, 4, 7, 8, 10, 11],
    venus: [2, 5, 6, 9, 10, 11],
    saturn: [3, 5, 6, 12],
    asc: [1, 2, 4, 5, 6, 7, 9, 10, 11],
  },
  venus: {
    sun: [8, 11, 12],
    moon: [1, 2, 3, 4, 5, 8, 9, 11, 12],
    mars: [3, 5, 6, 9, 11, 12],
    mercury: [3, 5, 6, 9, 11],
    jupiter: [5, 8, 9, 10, 11],
    venus: [1, 2, 3, 4, 5, 8, 9, 10, 11],
    saturn: [3, 4, 5, 8, 9, 10, 11],
    // Classical Lagna row includes the 11th (1,2,3,4,5,8,9,11) — Venus totals 52 and
    // the Sarvashtakavarga grand total lands on the canonical 337.
    asc: [1, 2, 3, 4, 5, 8, 9, 11],
  },
  saturn: {
    sun: [1, 2, 4, 7, 8, 10, 11],
    moon: [3, 6, 11],
    mars: [3, 5, 6, 10, 11, 12],
    mercury: [6, 8, 9, 10, 11, 12],
    jupiter: [5, 6, 11, 12],
    venus: [6, 11, 12],
    saturn: [3, 5, 6, 11],
    asc: [1, 3, 4, 6, 10, 11],
  },
};

// Per-planet Bhinnashtakavarga maxima (sum of one planet's chart over all 12 signs) — the
// canonical Parashari totals; grand total 337. A totals checksum alone cannot catch a
// same-size cell slip (e.g. "2" for "12"), so tests/test_ashtakavarga.py also pins every
// row of BAV_TABLE cell-by-cell against the classical tables.
export const BHINNA_TOTALS: Record<string, number> = {
  sun: 48, moon: 49, mars: 39, mercury: 54, jupiter: 56, venus: 52, saturn: 39,
};

// Gate shape. rel = (house SAV − chart mean SAV) / mean; the factor is 1 + clamp(rel)·GAIN,
// clamped to [FLOOR, CEIL]. Gentle by design — a confirmation nudge, never a primary judge.
const _SAV_GAIN = 0.6;
const _SAV_FLOOR = 0.85;
const _SAV_CEIL = 1.15;
const _SAV_REL_CLAMP = 0.25;

// Ablation switch for the benchmark harness only (see lib/strength_scoring GATING_ENABLED). When
// False, the delivery multipliers return 1.0 so the harness can isolate the Ashtakavarga layer's
// accuracy contribution. Always True in production.
export let GATING_ENABLED = true;

export function set_gating_enabled(value: boolean): void {
  GATING_ENABLED = value;
}

function _source_signs(chart: RawChartData): Record<string, number> {
  const signs: Record<string, number> = {};
  for (const name of BAV_PLANETS) {
    const pos = chart.planets[name];
    if (pos === undefined) {
      return {};
    }
    signs[name] = Math.trunc(pos.sign);
  }
  const asc = chart.planets["ascendant"];
  if (asc === undefined) {
    return {};
  }
  signs["asc"] = Math.trunc(asc.sign);
  return signs;
}

/**
 * Each of the seven planets' Bhinnashtakavarga as a 12-length list indexed by sign (0=Aries).
 *
 * In planet P's chart, every contributing source S awards a Rekha to each sign that falls at
 * one of S's benefic positions COUNTED FROM S's own natal sign. So a source at sign ss awards
 * +1 to sign (ss + position − 1) mod 12 for each benefic `position` in P's table for S. Summing
 * over the eight sources reproduces P's documented total (BHINNA_TOTALS).
 */
export function bhinnashtakavarga(chart: RawChartData): Record<string, number[]> {
  const source_signs = _source_signs(chart);
  const out: Record<string, number[]> = {};
  if (!Object.keys(source_signs).length) {
    for (const p of BAV_PLANETS) {
      out[p] = new Array(12).fill(0);
    }
    return out;
  }
  for (const planet of BAV_PLANETS) {
    const chart_bav = new Array(12).fill(0);
    const table = BAV_TABLE[planet];
    for (const source of _SOURCES) {
      const ss = source_signs[source];
      for (const position of table[source]) {
        chart_bav[pymod(ss + (position - 1), 12)] += 1;
      }
    }
    out[planet] = chart_bav;
  }
  return out;
}

// Sarvashtakavarga is a pure function of the chart, but the two delivery multipliers below are
// called PER EVENT PER CANDIDATE — memoise per chart object (same pattern as
// yoga_scoring._chart_yoga_cache / strength_scoring._war_cache) so a candidate's scan pays for
// the eight-source table walk once, not once per event per multiplier.
const _sav_cache = new WeakMap<RawChartData, Record<number, number>>();

/**
 * Combined 7-planet Sarvashtakavarga: total Rekhas per sign (0=Aries .. 11=Pisces),
 * summed across the seven Bhinnashtakavarga charts. Max 337 across all signs; ~28 average.
 */
export function sarvashtakavarga(chart: RawChartData): Record<number, number> {
  let sav = _sav_cache.get(chart);
  if (sav !== undefined) {
    return sav;
  }
  const bav = bhinnashtakavarga(chart);
  sav = {};
  for (let sign = 0; sign < 12; sign++) {
    sav[sign] = 0;
  }
  for (const chart_bav of Object.values(bav)) {
    for (let sign = 0; sign < 12; sign++) {
      sav[sign] += chart_bav[sign];
    }
  }
  _sav_cache.set(chart, sav);
  return sav;
}

/** Whole-sign: the zodiac sign on the given house from the Ascendant. */
export function house_sign(chart: RawChartData, house: number): number {
  const asc_sign = Math.trunc(chart.planets["ascendant"].sign);
  return pymod(asc_sign + (Math.trunc(house) - 1), 12);
}

function _relative_factor(value: number, mean: number): number {
  if (mean <= 0) {
    return 1.0;
  }
  let rel = (value - mean) / mean;
  rel = Math.max(-_SAV_REL_CLAMP, Math.min(_SAV_REL_CLAMP, rel));
  return Math.max(_SAV_FLOOR, Math.min(_SAV_CEIL, 1.0 + rel * _SAV_GAIN));
}

/**
 * Gentle delivery multiplier (~0.85..1.15) for an event judged from `house`, from that
 * house's Sarvashtakavarga relative to the chart's own mean.
 *
 * A house carrying above-average Rekhas has the strength to deliver its event; a weak house
 * damps it. This rewards candidate ascendants where the event's house is genuinely strong.
 * (We score event PLAUSIBILITY: an active/strong relevant house supports the dated event
 * having occurred, so the boost direction is the same for benefic and malefic events.)
 */
export function event_house_sav_multiplier(
  chart: RawChartData,
  house: number,
): [number, Record<string, any>] {
  if (!GATING_ENABLED) {
    return [1.0, { disabled: true }];
  }
  const sav = sarvashtakavarga(chart);
  const mean = Object.values(sav).reduce((a, b) => a + b, 0) / 12.0;
  const sign = house_sign(chart, house);
  const value = sav[sign];
  const factor = _relative_factor(value, mean);
  return [
    factor,
    { house: Math.trunc(house), house_sign: sign, sav: value, mean_sav: pythonRound(mean, 1) },
  ];
}

// ───────────────────────────── Shodhana (09_ashtakavarga_shodhana_rectification.md) ─────────────────────────────
//
// READING TAKEN (documented per task requirement): the Shodhana doc defines Trikona and
// Ekadhipatya reductions on each PLANET's Bhinnashtakavarga and states "the resulting values are
// the Shodhita AshtakaVarga — the operative scores used for all prediction, longevity calculation,
// and Pinda calculation. Raw scores before Shodhana are NOT used for final interpretation."
// Both reduction procedures are defined per-planet-chart (per-BAV); the doc nowhere defines a
// Shodhana of the combined Sarvashtakavarga, and the SAV thresholds the transit doc quotes
// (30+/25 dots — 09_ashtakavarga_transit_rules.md, Moon Muhurta + Saturn rules) are on the RAW
// SAV scale (~28 mean), which shodhita values (heavily reduced) could never reach. So:
//   • the existing SAV-based gates (event_house_sav_multiplier, dasha_lord_sav_multiplier)
//     stay on raw Sarvashtakavarga, byte-identical to before;
//   • shodhita_bav() below exposes the per-planet SHODHITA values for BAV-based prediction
//     (the doc's "operative scores"), available to the transit layer and future consumers.
// The doc's "Planet in Own Sign (Exaltation Special Case)" +1 record is marked UNVERIFIED in the
// source itself ("do not rely on it for calculation") and is deliberately NOT implemented — only
// the two classical, purely reductive procedures are.

// The four element trines by 0-indexed sign: Fire (Aries/Leo/Sagittarius), Earth
// (Taurus/Virgo/Capricorn), Air (Gemini/Libra/Aquarius), Water (Cancer/Scorpio/Pisces).
const _TRINES: readonly (readonly number[])[] = [
  [0, 4, 8],
  [1, 5, 9],
  [2, 6, 10],
  [3, 7, 11],
];

// The five dual-ruling planets and their sign pairs (from SIGN_RULERS — Vedic rulerships).
// Sun (Leo) and Moon (Cancer) rule only one sign — "no Ekadhipatya Shodhana applies to them"
// (doc, Both Signs Vacant record, Exceptions).
const _DUAL_LORD_SIGNS: readonly (readonly [number, number])[] = (() => {
  const by_lord: Record<string, number[]> = {};
  for (let sign = 0; sign < 12; sign++) {
    const lord = SIGN_RULERS[sign];
    (by_lord[lord] ??= []).push(sign);
  }
  return Object.values(by_lord)
    .filter((signs) => signs.length === 2)
    .map((signs) => [signs[0], signs[1]] as const);
})();

/**
 * Trikona Shodhana on one planet's 12-sign BAV (doc: "Trikona Shodhana — Fire/Earth/Air/Water
 * Trine Rectification"). For each element trine independently:
 *   • "find minimum score among the three ... Subtract the minimum from all three signs."
 *   • "Special case: if any sign in the trine scores 0, do NOT apply Shodhana to that trine."
 *   • "Special case: if all three signs have equal scores, subtract all values → set all to 0."
 *     (Arithmetically the same as subtracting the min; the zero-rule is checked FIRST, so an
 *     all-zero trine is simply left untouched — both rules agree on the result.)
 * Returns a new array; the input is not mutated.
 */
export function trikona_shodhana(bav: readonly number[]): number[] {
  const out = [...bav];
  for (const trine of _TRINES) {
    const values = trine.map((s) => out[s]);
    // "Exceptions: Zero in any sign of the trine = no Shodhana for that trine."
    if (values.some((v) => v === 0)) {
      continue;
    }
    // "Subtract the minimum from all three signs in the group." (All-equal → all become 0,
    // exactly the doc's equal-scores special case.)
    const min = Math.min(...values);
    for (const s of trine) {
      out[s] -= min;
    }
  }
  return out;
}

/**
 * Ekadhipatya Shodhana on one planet's BAV, applied AFTER Trikona Shodhana (doc: "Applied as
 * second rectification step after Trikona Shodhana"). `occupied[sign]` = a natal planet sits in
 * that sign (the seven classical AV planets — Rahu/Ketu are outside Ashtakavarga, Vol_09 §3.2).
 * For each dual-ruling planet's sign pair, per the doc's five records:
 *   • both values > 0 required ("Only applied when both values > 0") — else no change;
 *   • both occupied → "No Shodhana — keep both values";
 *   • both vacant, equal values → "Set both signs to zero";
 *   • both vacant, unequal → "Give BOTH signs the smaller of their two ... values";
 *   • one occupied with the LARGER value → "Keep the occupied sign's value ... Set the vacant
 *     sign's value to zero";
 *   • one occupied, vacant larger → "Subtract the occupied sign's value from the vacant sign's
 *     value; vacant sign gets the remainder. Occupied sign's value stays unchanged."
 *     (One occupied with EQUAL values is not separately listed; both adjacent rules converge on
 *     vacant → 0, which is what this implementation yields.)
 * Returns a new array; the input is not mutated.
 */
export function ekadhipatya_shodhana(bav: readonly number[], occupied: readonly boolean[]): number[] {
  const out = [...bav];
  for (const [a, b] of _DUAL_LORD_SIGNS) {
    const va = out[a];
    const vb = out[b];
    // "Exceptions: Only applied when both values > 0." (The both-vacant-equal record's
    // set-to-zero is a no-op when both are already 0, so the guard loses nothing.)
    if (!(va > 0 && vb > 0)) {
      continue;
    }
    const oa = occupied[a];
    const ob = occupied[b];
    if (oa && ob) {
      // "Both Signs Occupied ... No Shodhana."
      continue;
    }
    if (!oa && !ob) {
      if (va === vb) {
        // "Both Vacant, Equal Values ... Set both signs to zero."
        out[a] = 0;
        out[b] = 0;
      } else {
        // "Both Signs Vacant ... Give BOTH signs the smaller of their two ... values."
        const smaller = Math.min(va, vb);
        out[a] = smaller;
        out[b] = smaller;
      }
      continue;
    }
    // Exactly one occupied.
    const occ_sign = oa ? a : b;
    const vac_sign = oa ? b : a;
    if (out[occ_sign] >= out[vac_sign]) {
      // "Occupied Sign Has Larger Value ... Keep the occupied sign's value as-is. Set the
      // vacant sign's value to zero." (Equal converges here — see docstring.)
      out[vac_sign] = 0;
    } else {
      // "Vacant Sign Has Larger Value ... vacant sign gets the remainder."
      out[vac_sign] = out[vac_sign] - out[occ_sign];
    }
  }
  return out;
}

const _shodhita_cache = new WeakMap<RawChartData, Record<string, number[]>>();

/**
 * Shodhita AshtakaVarga — each planet's BAV after Trikona then Ekadhipatya Shodhana
 * (doc: "After both Trikona and Ekadhipatya Shodhana are complete ... the operative scores").
 * A NEW export: the raw bhinnashtakavarga()/sarvashtakavarga() outputs are untouched (the SAV
 * gates interpret raw Sarvashtakavarga — see the reading note above).
 */
export function shodhita_bav(chart: RawChartData): Record<string, number[]> {
  let cached = _shodhita_cache.get(chart);
  if (cached !== undefined) {
    return cached;
  }
  const source_signs = _source_signs(chart);
  const occupied = new Array(12).fill(false);
  for (const name of BAV_PLANETS) {
    const sign = source_signs[name];
    if (sign !== undefined) {
      occupied[pymod(sign, 12)] = true;
    }
  }
  const bav = bhinnashtakavarga(chart);
  const out: Record<string, number[]> = {};
  for (const planet of BAV_PLANETS) {
    out[planet] = ekadhipatya_shodhana(trikona_shodhana(bav[planet]), occupied);
  }
  _shodhita_cache.set(chart, out);
  return out;
}

// ───────────────────────── Kaksha system (09_ashtakavarga_transit_rules.md) ─────────────────────────
//
// "Each sign is divided into 8 equal parts of 3°45' each, ruled in sequence by: Saturn, Jupiter,
// Mars, Sun, Venus, Mercury, Moon, Ascendant." The kaksha refinement checks whether the kaksha
// ruler CONTRIBUTED a bindu (Rekha) to that sign in the transiting planet's own BAV — so the
// transit layer needs the per-source contribution matrix, not just the summed counts.

/** Kaksha rulers in fixed classical order (1st..8th kaksha of every sign). */
export const KAKSHA_RULERS = ["saturn", "jupiter", "mars", "sun", "venus", "mercury", "moon", "asc"] as const;

/** One kaksha spans 3°45' = 3.75°. */
export const KAKSHA_SPAN_DEG = 3.75;

/**
 * 0-based kaksha index (0..7) within a sign for an absolute ecliptic longitude.
 * Boundaries per the doc: 0°–3°45' → 0 (Saturn) ... 26°15'–30° → 7 (Ascendant).
 */
export function kaksha_index(longitude: number): number {
  const in_sign = pymod(longitude, 30.0);
  return Math.min(7, Math.trunc(in_sign / KAKSHA_SPAN_DEG));
}

/** The kaksha ruler ("saturn".."moon" or "asc") for an absolute ecliptic longitude. */
export function kaksha_ruler(longitude: number): string {
  return KAKSHA_RULERS[kaksha_index(longitude)];
}

const _contributors_cache = new WeakMap<RawChartData, Record<string, string[][]>>();

/**
 * Per-source bindu contribution matrix: for each planet's BAV, WHICH of the eight sources
 * (seven planets + "asc") awarded the bindus in each sign. contributors[planet][sign] lists the
 * contributing source names; its length always equals bhinnashtakavarga(chart)[planet][sign]
 * (each source contributes at most one bindu per sign per chart — the benefic-position lists
 * contain no duplicates). Returns all-empty lists when the chart is missing any of the eight
 * sources, mirroring bhinnashtakavarga's all-zero fallback. NEW export — existing outputs are
 * untouched (the 337-total behaviour of bhinnashtakavarga/sarvashtakavarga is byte-identical).
 */
export function bhinnashtakavarga_contributors(chart: RawChartData): Record<string, string[][]> {
  let cached = _contributors_cache.get(chart);
  if (cached !== undefined) {
    return cached;
  }
  const source_signs = _source_signs(chart);
  const out: Record<string, string[][]> = {};
  for (const planet of BAV_PLANETS) {
    const per_sign: string[][] = Array.from({ length: 12 }, () => []);
    if (Object.keys(source_signs).length) {
      const table = BAV_TABLE[planet];
      for (const source of _SOURCES) {
        const ss = source_signs[source];
        for (const position of table[source]) {
          per_sign[pymod(ss + (position - 1), 12)].push(source);
        }
      }
    }
    out[planet] = per_sign;
  }
  _contributors_cache.set(chart, out);
  return out;
}

/**
 * Vol_09 §11.3 synthesis rule: the Sarvashtakavarga of the dasha lord's natal sign modifies
 * how fully that dasha delivers. A lord sitting in a high-SAV sign runs an excellent period;
 * a low-SAV sign disappoints. Returns a gentle multiplier and detail; 1.0 for unknown lords.
 */
export function dasha_lord_sav_multiplier(
  chart: RawChartData,
  lord: string | null | undefined,
): [number, Record<string, any>] {
  if (!GATING_ENABLED) {
    return [1.0, { disabled: true }];
  }
  const g = (lord || "").trim().toLowerCase();
  const pos = chart.planets[g];
  if (pos === undefined) {
    return [1.0, {}];
  }
  const sav = sarvashtakavarga(chart);
  const mean = Object.values(sav).reduce((a, b) => a + b, 0) / 12.0;
  const sign = Math.trunc(pos.sign);
  const value = sav[sign];
  const factor = _relative_factor(value, mean);
  return [factor, { lord: g, lord_sign: sign, sav: value, mean_sav: pythonRound(mean, 1) }];
}
